import React, { useMemo, Suspense, lazy, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useApplication } from './Contexts/ApplicationContext';
import { useNFT } from './Contexts/SettingsContext';
import { Page } from '../Services/controllers/StepController';
import { PageLayout } from './Components/PageLayout';
import { useAnalytics } from './Contexts/AnalyticsContext';
import { SCREEN_EVENTS } from '../Services/analytics/EventTaxonomy';
const ThreeDSPaymentPage = lazy(() => import('./Pages/PaymentPage/ThreeDSPaymentPage'));
const PendingTransactionPage = lazy(() => import('./Pages/PaymentPage/TransactionPage/PendingTransactionPage'));
const ErrorTransactionPage = lazy(() => import('./Pages/PaymentPage/TransactionPage/ErrorTransactionPage'));
const AddressPageManager = lazy(() => import('./Pages/AddressPage'));
const SumsubKYCPage = lazy(() => import('./Pages/KYC/SumsubKYCPage'));
const SimpleKYCPage = lazy(() => import('./Pages/KYC/SimpleKYCPage'));
const PendingKYCPage = lazy(() => import('./Pages/KYC/PendingKYCPage'));
const CalculationPage = lazy(() => import('./Pages/CalculationPage'));
const PhonePage = lazy(() => import('./Pages/PhonePage'));
const OtpPhonePage = lazy(() => import('./Pages/PhonePage/OtpPhonePage'));
const EmailPage = lazy(() => import('./Pages/EmailPage'));
const OtpEmailPage = lazy(() => import('./Pages/EmailPage/OtpEmailPage'));
const NFTPage = lazy(() => import('./Pages/NFTPage'));
const CheckOrderManager = lazy(() => import('./Pages/CheckOrder'));
const PaymentPage = lazy(() => import('./Pages/PaymentPage'));
const BlockedPage = lazy(() => import('./Pages/BlockedPage'));
const OrderDetailsPage = lazy(() => import('./Pages/OrderDetailsPage'));
const PreconditionFailedPage = lazy(() => import('./Pages/PreconditionFailedPage'));
const ErrorPage = lazy(() => import('./Pages/ErrorPage/ErrorPage'));
const PaymentWirePending = lazy(() => import('./Pages/PaymentWirePending'));
export const PageManager = observer(() => {
    const { stepController } = useApplication();
    const { track } = useAnalytics();
    const step = stepController.currentStep;
    const smartContractData = useNFT();
    const previousStepRef = useRef(null);
    const transitionStartTimeRef = useRef(Date.now());
    useEffect(() => {
        const currentPage = step.step;
        const previousPage = previousStepRef.current;
        const transitionStartTime = transitionStartTimeRef.current;
        const transitionDuration = Date.now() - transitionStartTime;
        // Track legacy analytics for backwards compatibility
        track('user_change_step', {
            new_step: currentPage,
        });
        // Track screen transition completion
        if (previousPage !== null && previousPage !== currentPage) {
            // Statsig tracking removed to prevent excessive network requests
        }
        // Track screen view event with human-readable name
        const screenEvent = getScreenViewEvent(currentPage);
        if (screenEvent) {
            // Statsig tracking removed to prevent excessive network requests
        }
        // Update refs for next transition
        previousStepRef.current = currentPage;
        transitionStartTimeRef.current = Date.now();
    }, [step, track, smartContractData]);
    // Track screen transition start when step is changing
    useEffect(() => {
        const currentPage = step.step;
        const previousPage = previousStepRef.current;
        if (previousPage !== null && previousPage !== currentPage) {
            // Statsig tracking removed to prevent excessive network requests
        }
    }, [step.step]);
    const PageResult = useMemo(() => {
        switch (step.step) {
            case Page.MAIN:
                return smartContractData ? (<NFTPage {...smartContractData}/>) : (<CalculationPage />);
            case Page.PHONE:
                return <PhonePage />;
            case Page.PHONE_OTP:
                return <OtpPhonePage />;
            case Page.EMAIL_ADDRESS:
                return <EmailPage />;
            case Page.EMAIL_ADDRESS_OTP:
                return <OtpEmailPage />;
            case Page.TARGET_ADDRESS:
                return <AddressPageManager />;
            case Page.KYC:
                return <SumsubKYCPage />;
            case Page.KYC_SIMPLE:
                return <SimpleKYCPage />;
            case Page.KYC_PENDING:
                return <PendingKYCPage />;
            case Page.CHECK_ORDER:
                return <CheckOrderManager />;
            case Page.PAYMENT:
                return <PaymentPage />;
            case Page.BLOCKED:
                return <BlockedPage />;
            case Page.PAYMENT_PENDING:
                return <PendingTransactionPage />;
            case Page.PAYMENT_FAILED:
                return (<ErrorTransactionPage errorCode={'paymentFailureClass' in step ? step.paymentFailureClass : ''}/>);
            case Page.SUCCESS:
            case Page.ORDER_PLACED:
                return <OrderDetailsPage />;
            case Page.FAILED:
                if ('failureType' in step &&
                    step.failureType === 'WITHDRAWAL_PRECONDITION') {
                    return (<PreconditionFailedPage failureMessage={step.failureMessage}/>);
                }
                return <OrderDetailsPage />;
            case Page.PAYMENT_3DS_REQUIRED:
                return <ThreeDSPaymentPage />;
            case Page.PAYMENT_WIRE_PENDING:
                return <PaymentWirePending />;
            default:
                return <ErrorPage showReloadButton/>;
        }
    }, [smartContractData, step]);
    return (<Suspense fallback={<PageLayout loading hideBackArrow hideHeaderChip/>}>
      {PageResult}
    </Suspense>);
});
/**
 * Get human-readable page name for analytics
 */
function getHumanReadablePageName(page) {
    const pageNames = {
        [Page.MAIN]: 'Exchange Calculator',
        [Page.PHONE]: 'Phone Verification',
        [Page.PHONE_OTP]: 'Phone OTP Verification',
        [Page.EMAIL_ADDRESS]: 'Email Verification',
        [Page.EMAIL_ADDRESS_OTP]: 'Email OTP Verification',
        [Page.TARGET_ADDRESS]: 'Wallet Address Entry',
        [Page.KYC]: 'Identity Verification',
        [Page.KYC_SIMPLE]: 'Simplified Identity Verification',
        [Page.KYC_PENDING]: 'Identity Verification Pending',
        [Page.CHECK_ORDER]: 'Order Review',
        [Page.PAYMENT]: 'Payment Method Selection',
        [Page.PAYMENT_3DS_REQUIRED]: '3DS Payment Authentication',
        [Page.BLOCKED]: 'Account Blocked',
        [Page.PAYMENT_PENDING]: 'Payment Processing',
        [Page.PAYMENT_FAILED]: 'Payment Failed',
        [Page.SUCCESS]: 'Transaction Successful',
        [Page.ORDER_PLACED]: 'Order Confirmation',
        [Page.FAILED]: 'Transaction Failed',
        [Page.PAYMENT_WIRE_PENDING]: 'Wire Transfer Pending',
    };
    return pageNames[page] || 'Unknown Page';
}
/**
 * Get appropriate screen view event for analytics
 */
function getScreenViewEvent(page) {
    const eventMap = {
        [Page.MAIN]: SCREEN_EVENTS.MAIN_SCREEN_VIEWED,
        [Page.PHONE]: SCREEN_EVENTS.PHONE_SCREEN_VIEWED,
        [Page.PHONE_OTP]: SCREEN_EVENTS.PHONE_OTP_SCREEN_VIEWED,
        [Page.EMAIL_ADDRESS]: SCREEN_EVENTS.EMAIL_SCREEN_VIEWED,
        [Page.EMAIL_ADDRESS_OTP]: SCREEN_EVENTS.EMAIL_OTP_SCREEN_VIEWED,
        [Page.TARGET_ADDRESS]: SCREEN_EVENTS.ADDRESS_SCREEN_VIEWED,
        [Page.KYC]: SCREEN_EVENTS.KYC_SCREEN_VIEWED,
        [Page.KYC_SIMPLE]: SCREEN_EVENTS.KYC_SIMPLE_SCREEN_VIEWED,
        [Page.KYC_PENDING]: SCREEN_EVENTS.KYC_PENDING_SCREEN_VIEWED,
        [Page.CHECK_ORDER]: SCREEN_EVENTS.ORDER_REVIEW_SCREEN_VIEWED,
        [Page.PAYMENT]: SCREEN_EVENTS.PAYMENT_SCREEN_VIEWED,
        [Page.PAYMENT_3DS_REQUIRED]: SCREEN_EVENTS.PAYMENT_3DS_SCREEN_VIEWED,
        [Page.BLOCKED]: SCREEN_EVENTS.BLOCKED_SCREEN_VIEWED,
        [Page.PAYMENT_PENDING]: SCREEN_EVENTS.PAYMENT_PENDING_SCREEN_VIEWED,
        [Page.PAYMENT_FAILED]: SCREEN_EVENTS.FAILED_SCREEN_VIEWED,
        [Page.SUCCESS]: SCREEN_EVENTS.SUCCESS_SCREEN_VIEWED,
        [Page.ORDER_PLACED]: SCREEN_EVENTS.SUCCESS_SCREEN_VIEWED,
        [Page.FAILED]: SCREEN_EVENTS.FAILED_SCREEN_VIEWED,
        [Page.PAYMENT_WIRE_PENDING]: SCREEN_EVENTS.WIRE_PENDING_SCREEN_VIEWED,
    };
    return eventMap[page] || null;
}
/**
 * Determine transition type for analytics
 */
function determineTransitionType(fromPage, toPage) {
    // Define the typical flow order
    const flowOrder = [
        Page.MAIN,
        Page.PHONE,
        Page.PHONE_OTP,
        Page.EMAIL_ADDRESS,
        Page.EMAIL_ADDRESS_OTP,
        Page.TARGET_ADDRESS,
        Page.KYC,
        Page.KYC_SIMPLE,
        Page.KYC_PENDING,
        Page.CHECK_ORDER,
        Page.PAYMENT,
        Page.PAYMENT_3DS_REQUIRED,
        Page.PAYMENT_PENDING,
        Page.SUCCESS,
        Page.ORDER_PLACED,
    ];
    const fromIndex = flowOrder.indexOf(fromPage);
    const toIndex = flowOrder.indexOf(toPage);
    // Error/blocked pages
    if (toPage === Page.BLOCKED || toPage === Page.PAYMENT_FAILED || toPage === Page.FAILED) {
        return 'error_transition';
    }
    // Success pages
    if (toPage === Page.SUCCESS || toPage === Page.ORDER_PLACED) {
        return 'completion_transition';
    }
    // Normal flow progression
    if (fromIndex >= 0 && toIndex >= 0) {
        if (toIndex > fromIndex) {
            return 'forward_transition';
        }
        else if (toIndex < fromIndex) {
            return 'backward_transition';
        }
        else {
            return 'same_page_transition';
        }
    }
    // Special transitions
    if (fromPage === Page.KYC && toPage === Page.KYC_PENDING) {
        return 'verification_pending_transition';
    }
    if (fromPage === Page.PAYMENT && toPage === Page.PAYMENT_3DS_REQUIRED) {
        return 'authentication_required_transition';
    }
    return 'unknown_transition';
}
